import type { MaybeRef } from "@vueuse/core";
import type { Sizing } from "@/composables/forms/formTypes";
import { ref, computed } from "vue";
import { injectStrict } from "@/utils/utils";
import { ToUnit } from "@/libs/units";
import type { UnitPresets } from "@/libs/units/presets";
import { loadUnitPreset } from "@/libs/units/presets";
import { sizingKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useTargetUnits } from "./useTargetUnits";

export const useSizingUnits = (
  sizing?: MaybeRef<Sizing | null | undefined>
) => {
  const _sizing = sizing ? ref(sizing) : injectStrict(sizingKey);

  const unitPreset = computed(() =>
    loadUnitPreset(_sizing.value?.default_display_units)
  );

  const getUnitByKey = (genericKey: keyof UnitPresets) =>
    computed(() => {
      const sizingUnit = ToUnit(unitPreset.value[genericKey]);
      if (!sizingUnit) throw new Error("Unit Not Found");
      return sizingUnit;
    });

  const targetUnits = useTargetUnits(getUnitByKey);

  /**
    In general, these are the input keys we use as the 'master' ones for sizing units.
    getLargeLengthUnit("Required TDH.convert.Feet");
    getSmallLengthUnit('Shroud Interior Diameter.write.Inches')
    getDensityUnit("Discharge Fluid Density.convert.lb/ft³")
    getConcentrationUnit none but should be "Water Salinity.write.ppm"
    getLiquidFlowUnit("Target Liquid Rate.convert.STB/D")
    getProductivityFlowUnit("Productivity Index.write.stb/d/psi");
    getGasFlowUnit("Gas Rate.write.mscf/d");
    getPressureUnit("Producing Bottomhole Pressure at Datum [Pwf].write.PSIA")
    getTempUnit - none but should have one
    getVoltageUnit("Surface Voltage.convert.V");
    getHydraulicPowerUnit("Shaft Limit.convert.hp");
    getRealPowerUnit none but should be 'System Power.convert.kW'
    getApparentPowerUnit none but should be 'Required kVA.convert.kVA'
    getSpeedUnit("Flow by Motor.convert.ft/s");
    getCurrentUnit("Motor Current.convert.A");
    getViscosityUnit none but should have one
    getMassUnit("Weight.convert.lbs")
    getVolumeUnit none but it should have one
   */
  return {
    targetLargeLengthUnit: targetUnits.getLargeLengthUnit(),
    targetSmallLengthUnit: targetUnits.getSmallLengthUnit(),
    targetLiquidFlowUnit: targetUnits.getLiquidFlowUnit(),
    targetGasFlowUnit: targetUnits.getGasFlowUnit(),
    targetProductivityFlowUnit: targetUnits.getProductivityFlowUnit(),
    targetPressureUnit: targetUnits.getPressureUnit(),
    targetTempUnit: targetUnits.getTempUnit(),
    targetDensityUnit: targetUnits.getDensityUnit(),
    targetCurrentUnit: targetUnits.getCurrentUnit(),
    targetSpeedUnit: targetUnits.getSpeedUnit(),
    targetVoltageUnit: targetUnits.getVoltageUnit(),
    targetViscosityUnit: targetUnits.getViscosityUnit(),
    targetLiquidRatioUnit: targetUnits.getLiquidRatioUnit(),
    targetMassUnit: targetUnits.getMassUnit(),
    targetHydraulicPowerUnit: targetUnits.getHydraulicPowerUnit(),
    targetRealPowerUnit: targetUnits.getRealPowerUnit(),
    targetApparentPowerUnit: targetUnits.getApparentPowerUnit(),
    targetVolumeUnit: targetUnits.getVolumeUnit(),
    targetConcentrationUnit: targetUnits.getConcentrationUnit()
  };
};
